import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { SectionService } from "../../../services/sections/serction.service";
import { SectionModel } from "../../../services/sections/models/section.model";
import { TableService } from "../../../services/tables/table.service";
import { TableModel } from "../../../services/tables/models/tabele.model";
import { OrderStateModel } from "../../../services/orders/models/order_state.model";
import { OrderService } from "../../../services/orders/order.service";
import { OrderProductStatusModel } from "../../../services/order_product_statuses/models/order_product_status.model";
import { OrderProductStatusService } from "../../../services/order_product_statuses/order_product_status.service";
import { OrderProductStatusEnum } from "../../../enums/orders/order_status.enum";
import { WaiterModel } from "../../../models/waiter.model";
import { WaiterService } from "../../../services/waiters/waiter.service";
import * as signalR from "@microsoft/signalr";
import { WaiterHubs } from "../../../consts/SignaIR.const";
import { WaiterHubEnum } from "../../../enums/waiter_hub.enum";
import { OrderHubUrl } from "../../../consts/api";
import { toast } from "react-toastify";
import MenuProductServcie from "../../../services/menu_products/menu_product.service";
import MenuCategoryService from "../../../services/menu_categories/menu_category.service";
import { PrinterService } from "../../../services/printers/printer.service";
import { AppDispatch } from "../../../react-reduxs/configure_store";
import { setProductList } from "../../../react-reduxs/slices/products/product_slice";
import { setCategoryList } from "../../../react-reduxs/slices/categories/category_slice";
import { setPrinterList } from "../../../react-reduxs/slices/printers/printer_slice";
import { useDispatch, useSelector } from "react-redux";
import {
  getCompanyId,
  getUserId,
} from "../../../react-reduxs/slices/auth/auth_slice";
import { requestNotificationPermission } from "../../../firebase/firebase-init";
import NotificationSettingServcie from "../../../services/notification_settings/notification_setting.service";
import { NotificationSettingModel } from "../../../services/notification_settings/models/notification-setting.model";

// ─── Waiter Alt Sayfaları ─────────────────────────────────────────────────────
import { Page } from "./types";
import WaiterDashboard from "./WaiterDashboard";
import WaiterTables from "./WaiterTables";
import WaiterTableDetail from "./WaiterTableDetail";
import WaiterSections from "./WaiterSections";

// ─────────────────────────────────────────────────────────────────────────────

const WaiterHomePage2: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const userId = useSelector(getUserId);
  const companyId = useSelector(getCompanyId);

  const audioRef = useRef<HTMLAudioElement>(new Audio("/music/waiterCall.mp3"));

  // Ref'ler – SignalR callback'lerinde güncel değeri okumak için
  const tableListRef = useRef<TableModel[]>([]);
  const sectionListRef = useRef<SectionModel[]>([]);
  const orderStateRef = useRef<OrderStateModel[]>([]);

  // Servisler
  const sectionService = useMemo(() => new SectionService(), []);
  const tableService = useMemo(() => new TableService(), []);
  const orderService = useMemo(() => new OrderService(), []);
  const printerService = useMemo(() => new PrinterService(), []);
  const waiterService = useMemo(() => new WaiterService(), []);
  const orderProductStatusService = useMemo(
    () => new OrderProductStatusService(),
    [],
  );
  const menuProductService = useMemo(() => new MenuProductServcie(), []);
  const notificationSettingService = useMemo(
    () => new NotificationSettingServcie(),
    [],
  );
  const menuCategoryService = useMemo(() => new MenuCategoryService(), []);

  // State
  const [sections, setSections] = useState<SectionModel[]>([]);
  const [allTables, setAllTables] = useState<TableModel[]>([]);
  const [orderStateList, setOrderStateList] = useState<OrderStateModel[]>([]);
  const [waiterCallList, setWaiterCallList] = useState<WaiterModel[]>([]);
  const [orderProductStatusList, setOrderProductStatusList] = useState<
    OrderProductStatusModel[]
  >([]);
  const [hupStartedList, setHupStartedList] = useState<any[]>([]);
  const [initialized, setInitialized] = useState(false);

  // ─── Navigasyon ──────────────────────────────────────────────────────────────
  const [page, setPage] = useState<Page>({ name: "WaiterDashboard" });

  const navigate = useCallback((next: Page) => {
    setPage(next);
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // ─── Ses ─────────────────────────────────────────────────────────────────────
  const playNotification = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
      audioRef.current
        .play()
        .catch((err) => console.error("Audio Play Error:", err));
    }
  }, []);

  // ─── Sipariş sayacı (badge) ───────────────────────────────────────────────────
  const getOrderCount = useCallback(
    (sectionId: string): number => {
      let sum = 0;
      const secTables = tableListRef.current.filter(
        (t) => t.sectionId === sectionId,
      );
      secTables.forEach((t) => {
        const os = orderStateRef.current.find((o) => o.order.tableId === t.id);
        const calls = waiterCallList.filter((w) => w.tableId === t.id);
        sum += calls.length;
        if (os) {
          sum += os.orderProductList.filter(
            (op) => op.status.text === OrderProductStatusEnum.pendingApproval,
          ).length;
        }
      });
      return sum;
    },
    [waiterCallList],
  );

  // ─── Veri yükleyiciler ───────────────────────────────────────────────────────
  const getOrder = useCallback(async () => {
    const res = await orderService.getOrderList(companyId);
    if (res) {
      setOrderStateList(res);
      orderStateRef.current = res;
    }
  }, [companyId, orderService]);

  const getWaiterCall = useCallback(async () => {
    const res = await waiterService.getWaiterCallListByCompanyId(companyId);
    if (res) setWaiterCallList(res);
  }, [companyId, waiterService]);

  const getOrderProductStatusList = useCallback(async () => {
    const res = await orderProductStatusService.getList();
    if (res) setOrderProductStatusList(res);
  }, [orderProductStatusService]);

  const getProductList = useCallback(async () => {
    const res = await menuProductService.getListByStatus(companyId, true);
    if (res) dispatch(setProductList(res));
  }, [menuProductService, companyId, dispatch]);

  const getPrinterList = useCallback(async () => {
    const res = await printerService.getList(companyId);
    if (res) dispatch(setPrinterList(res));
  }, [printerService, companyId, dispatch]);

  const getCategoryList = useCallback(async () => {
    const res = await menuCategoryService.getListByStatus(companyId, true);
    if (res) dispatch(setCategoryList(res));
  }, [menuCategoryService, dispatch, companyId]);

  // ─── SignalR ──────────────────────────────────────────────────────────────────
  const getSignalRHub = useCallback(async () => {
    WaiterHubs.forEach(async (hup) => {
      const connection = new signalR.HubConnectionBuilder()
        .withAutomaticReconnect()
        .withUrl(hup.val(companyId).url)
        .build();

      const startConnection = async () => {
        try {
          await connection.start();
          await getOrder();
          await getWaiterCall();
        } catch {
          setTimeout(startConnection, 5000);
        }
      };

      connection.onclose(() => startConnection());
      await startConnection();

      if (hup.name === WaiterHubEnum.order) {
        connection.on("WaiterOrderList", (order, type) => {
          setOrderStateList(order);
          orderStateRef.current = order;
          if (type === "call") {
            playNotification();
            toast.info("Yeni sipariş geldi!");
          }
        });
      }

      if (hup.name === WaiterHubEnum.waiterCall) {
        connection.on(hup.val(companyId).event, (waiter, type) => {
          if (type === "call") {
            playNotification();
            const lastCall = waiter[waiter.length - 1];
            const table = tableListRef.current.find(
              (t) => t.id === lastCall?.tableId,
            );
            const section = sectionListRef.current.find(
              (s) => s.id === table?.sectionId,
            );
            if (Notification.permission === "granted") {
              new Notification("Garson Çağrı!", {
                body: `Bölüm: ${section?.name || ""} - Masa: ${table?.name || ""}`,
                icon: "/logo192.png",
              });
            }
          }
          setWaiterCallList(waiter);
        });
      }

      setHupStartedList((prev) => [...prev, connection]);
    });
  }, [companyId, getOrder, getWaiterCall, playNotification]);

  // ─── Bildirim token ───────────────────────────────────────────────────────────
  const onTokenCallback = useCallback(async () => {
    try {
      const token = await requestNotificationPermission();
      if (token && userId !== "") {
        notificationSettingService.add({
          userId,
          token,
          isActive: true,
        } as NotificationSettingModel);
      }
    } catch (err) {
      console.error("FCM Token Error:", err);
    }
  }, [userId, notificationSettingService]);

  // ─── İlk yükleme ─────────────────────────────────────────────────────────────
  const initialLoad = useCallback(async () => {
    if (!companyId) return;
    try {
      const sectionResponse =
        await sectionService.getListByCompanyId(companyId);
      if (sectionResponse.length > 0) {
        setSections(sectionResponse);
        sectionListRef.current = sectionResponse;
      }
      const tableResponse = await tableService.getListByCompanyId(companyId);
      if (tableResponse.length > 0) {
        tableListRef.current = tableResponse;
        setAllTables(tableResponse);
      }
      await getOrderProductStatusList();
      await getSignalRHub();
      await getProductList();
      await getCategoryList();
      await getPrinterList();
    } catch (err) {
      console.error("Initial Load Error:", err);
    }
  }, [
    companyId,
    sectionService,
    tableService,
    getOrderProductStatusList,
    getSignalRHub,
    getProductList,
    getCategoryList,
    getPrinterList,
  ]);

  useEffect(() => {
    if (!initialized) {
      setInitialized(true);
      initialLoad();
      onTokenCallback();
    }

    const unlockAudio = () => {
      audioRef.current
        .play()
        .then(() => audioRef.current.pause())
        .catch(() => {});
      window.removeEventListener("click", unlockAudio);
    };
    window.addEventListener("click", unlockAudio);

    return () => {
      window.removeEventListener("click", unlockAudio);
      hupStartedList.forEach((hub) => hub.stop());
    };
  }, [initialized, initialLoad, onTokenCallback, hupStartedList]);

  // ─── Ürün durum güncelleme (SignalR) ─────────────────────────────────────────
  const updateOrderProductStatus = useCallback(
    async (
      tableId: string,
      orderProductId: string,
      orderProductStatusId: number,
    ) => {
      const connection = new signalR.HubConnectionBuilder()
        .withUrl(OrderHubUrl(companyId).url)
        .withAutomaticReconnect()
        .build();
      try {
        await connection.start();
        await connection.invoke(
          "OrderProductStatusUpdate",
          companyId,
          tableId,
          orderProductId,
          orderProductStatusId,
        );
      } catch (err) {
        console.error(err);
      } finally {
        connection.stop();
      }
    },
    [companyId],
  );

  // ─── Ortak prop seti ──────────────────────────────────────────────────────────
  const sharedProps = {
    sections,
    allTables,
    orderStateList,
    waiterCallList,
    orderProductStatusList,
    navigate,
    updateOrderProductStatus,
    getOrderCount,
  };

  // ─── Sayfa render ─────────────────────────────────────────────────────────────
  switch (page.name) {
    case "WaiterDashboard":
      return <WaiterDashboard {...sharedProps} />;

    case "WaiterTables":
      return (
        <WaiterTables {...sharedProps} initialSectionId={page.sectionId} />
      );

    case "WaiterTableDetail":
      return (
        <WaiterTableDetail
          {...sharedProps}
          tableId={page.tableId}
          sectionId={page.sectionId}
          waiterModel={waiterCallList.find((x) => x.tableId === page.tableId)}
        />
      );

    case "WaiterSections":
      return (
        <WaiterSections
          sections={sections}
          companyId={companyId}
          navigate={navigate}
          onRefresh={initialLoad}
        />
      );
  }
};

export default WaiterHomePage2;
